import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface A2AMessengerProps {
  endpointUrl: string
  skillName?: string
  transport?: string  // jsonrpc, rest, grpc, etc.
  warning?: string  // Warning about the endpoint (e.g., looks like a website)
  examples?: string[]  // Example prompts/messages for the skill
  requiresPayment?: boolean  // True if endpoint returns 402 (x402)
  paymentInfo?: Record<string, unknown>  // x402 pricing details (accepts array, etc.)
  onClose: () => void
}

interface A2AMessage {
  role: 'user' | 'agent'
  content: string
  timestamp: Date
}

interface A2AResponse {
  success: boolean
  result?: unknown
  error?: string
  status?: number
  latency_ms?: number
  payment_required?: boolean
  payment_info?: Record<string, unknown>
}

export function A2AMessenger({ endpointUrl, skillName, transport, warning, examples, requiresPayment, paymentInfo, onClose }: A2AMessengerProps) {
  const [message, setMessage] = useState('')
  const [messages, setMessages] = useState<A2AMessage[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copiedAddress, setCopiedAddress] = useState(false)
  const [paymentPayload, setPaymentPayload] = useState('')

  const isValidUrl = endpointUrl && endpointUrl.startsWith('http')
  // Currently we only support jsonrpc transport (default when not specified)
  const transportLower = transport?.toLowerCase()
  const supportedTransport = !transport || transportLower?.includes('jsonrpc') || transportLower === 'http'
  const isUsable = isValidUrl && supportedTransport

  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim() || !isUsable) return

    const userMessage = messageText.trim()
    setMessage('')
    setError(null)

    // Add user message to chat
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    }])

    setSending(true)

    try {
      const startTime = Date.now()

      // A2A protocol: send task to endpoint
      const sendBody: Record<string, unknown> = {
        endpoint_url: endpointUrl,
        message: userMessage,
        skill: skillName,
      }
      if (paymentPayload.trim()) {
        sendBody.x_payment = paymentPayload.trim()
      }

      const response = await fetch('/api/v1/a2a/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sendBody),
      })

      const latencyMs = Date.now() - startTime
      const data = await response.json() as A2AResponse

      if (data.success && data.result) {
        // Extract text from response
        let responseText: string
        if (typeof data.result === 'string') {
          responseText = data.result
        } else if (typeof data.result === 'object') {
          const result = data.result as Record<string, unknown>
          // Try common response fields
          responseText = (result.text || result.message || result.content || result.response || JSON.stringify(result, null, 2)) as string
        } else {
          responseText = String(data.result)
        }

        setMessages(prev => [...prev, {
          role: 'agent',
          content: responseText + (latencyMs ? `\n\n(${latencyMs}ms)` : ''),
          timestamp: new Date()
        }])
      } else if (data.payment_required) {
        // 402 — show raw response from agent
        let paymentMsg = 'Payment required (HTTP 402).'
        if (data.payment_info && Object.keys(data.payment_info).length > 0) {
          paymentMsg += '\n\n' + JSON.stringify(data.payment_info, null, 2)
        }

        setMessages(prev => [...prev, {
          role: 'agent',
          content: paymentMsg,
          timestamp: new Date()
        }])
      } else if (data.error) {
        setError(data.error)
      } else {
        setError('No response from agent')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  // Wrapper to send the current message in the input field
  const handleSend = () => handleSendMessage(message)

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Handle clicking an example prompt
  const handleExampleClick = (example: string) => {
    handleSendMessage(example)
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-2xl max-h-[85vh] bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-default)] shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-[var(--border-subtle)] flex items-center justify-between flex-shrink-0">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-display font-medium text-[var(--text-primary)]">
                  A2A Messenger
                </h2>
                {requiresPayment && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-mono font-medium flex items-center gap-1">
                    <span className="text-sm">$</span>
                    x402
                  </span>
                )}
              </div>
              <p className="text-xs font-mono text-[var(--text-muted)] mt-0.5 truncate max-w-md">
                {endpointUrl}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              <XIcon className="w-5 h-5 text-[var(--text-muted)]" />
            </button>
          </div>

          {/* Warning Banner */}
          {warning && (
            <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 flex items-center gap-2">
              <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-xs font-mono text-amber-400">
                May fail: {warning}
              </p>
            </div>
          )}

          {/* x402 Payment Info Banner */}
          {requiresPayment && (
            <div className="px-4 py-3 bg-emerald-500/5 border-b border-emerald-500/20">
              <div className="flex items-start gap-2.5">
                <span className="text-emerald-400 text-sm mt-0.5 font-bold">$</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono text-emerald-400 font-medium">
                    This agent requires x402 payment
                  </p>
                  {(() => {
                    const pricing = formatPaymentSummary(paymentInfo)
                    const payTo = extractPayTo(paymentInfo)
                    const networks = extractNetworks(paymentInfo)
                    return (
                      <>
                        {pricing && (
                          <p className="text-xs font-mono text-[var(--text-muted)] mt-1.5">
                            Price: <span className="text-[var(--text-secondary)]">{pricing}</span>
                          </p>
                        )}
                        {networks && (
                          <p className="text-xs font-mono text-[var(--text-muted)] mt-0.5">
                            Network: <span className="text-[var(--text-secondary)]">{networks}</span>
                          </p>
                        )}
                        {payTo && (
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <p className="text-xs font-mono text-[var(--text-muted)] truncate">
                              Pay to: <span className="text-[var(--text-secondary)]">{payTo}</span>
                            </p>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(payTo)
                                setCopiedAddress(true)
                                setTimeout(() => setCopiedAddress(false), 2000)
                              }}
                              className="flex-shrink-0 p-1 rounded hover:bg-[var(--bg-tertiary)] transition-colors"
                              title="Copy address"
                            >
                              {copiedAddress ? (
                                <svg className="w-3 h-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-3 h-3 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                          </div>
                        )}
                      </>
                    )
                  })()}
                  <div className="mt-2.5">
                    <label className="text-[11px] font-mono text-[var(--text-muted)] block mb-1">
                      Signed x402 authorization (EIP-712)
                    </label>
                    <input
                      type="text"
                      value={paymentPayload}
                      onChange={(e) => setPaymentPayload(e.target.value)}
                      placeholder="Paste signed payload..."
                      spellCheck={false}
                      className="w-full px-3 py-2 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-default)] text-[var(--text-primary)] font-mono text-xs placeholder:text-[var(--text-muted)] focus:outline-none focus:border-emerald-500/50"
                    />
                    <p className="text-[10px] font-mono text-[var(--text-muted)] mt-1">
                      Sent as <span className="text-[var(--text-secondary)]">X-PAYMENT</span> header. In-browser wallet signing coming soon.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-auto p-4 space-y-4">
            {!isValidUrl ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-3">!</div>
                <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 px-4 py-3 rounded-xl text-sm font-mono inline-block">
                  <p className="font-medium mb-1">No messaging URL available</p>
                  <p className="text-xs text-amber-400/80">
                    The agent card does not specify a URL field for sending messages.
                  </p>
                </div>
              </div>
            ) : !supportedTransport ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-3">!</div>
                <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 px-4 py-3 rounded-xl text-sm font-mono inline-block">
                  <p className="font-medium mb-1">Unsupported transport: {transport}</p>
                  <p className="text-xs text-amber-400/80">
                    This agent uses a transport method we don't support yet. Only JSON-RPC is currently supported.
                  </p>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-muted)]">
                <div className="text-4xl mb-3">💬</div>
                <p className="font-mono text-sm">Send a message to start the conversation</p>
                {skillName && (
                  <p className="font-mono text-xs mt-2 text-[var(--accent-teal)]">
                    Skill: {skillName}
                  </p>
                )}

                {/* Example Prompts */}
                {examples && examples.length > 0 && (
                  <div className="mt-6">
                    <p className="font-mono text-xs text-[var(--text-muted)] mb-3 uppercase tracking-wide">
                      Try an example
                    </p>
                    <div className="flex flex-col gap-2 max-w-md mx-auto">
                      {examples.slice(0, 4).map((example, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleExampleClick(example)}
                          disabled={sending}
                          className="px-4 py-2.5 rounded-xl border border-[var(--border-default)] bg-[var(--bg-tertiary)] hover:bg-[var(--bg-primary)] hover:border-[var(--accent-teal)] transition-all text-left text-sm font-mono text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="text-[var(--accent-teal)] mr-2">→</span>
                          {example}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-[var(--accent-teal)] text-white rounded-br-md'
                        : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))
            )}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-[var(--bg-tertiary)] px-4 py-3 rounded-2xl rounded-bl-md">
                  <div className="flex items-center gap-2 text-[var(--text-muted)]">
                    <LoadingDots />
                    <span className="text-sm font-mono">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            {error && (
              <div className="flex justify-center">
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-2 rounded-xl text-sm font-mono">
                  {error}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="px-4 py-4 border-t border-[var(--border-subtle)] flex-shrink-0">
            <div className="flex gap-3">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isUsable ? "Type a message..." : "Messaging unavailable"}
                rows={1}
                disabled={!isUsable}
                className={`flex-1 px-4 py-3 rounded-xl bg-[var(--bg-tertiary)] border border-[var(--border-default)] text-[var(--text-primary)] font-mono text-sm placeholder:text-[var(--text-muted)] resize-none focus:outline-none focus:border-[var(--accent-teal)] ${!isUsable ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              <button
                onClick={handleSend}
                disabled={sending || !message.trim() || !isUsable}
                className="px-6 py-3 rounded-xl bg-[var(--accent-teal)] text-[var(--bg-primary)] font-mono text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
              >
                {sending ? (
                  <LoadingDots />
                ) : (
                  <SendIcon className="w-4 h-4" />
                )}
                Send
              </button>
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-2 text-center">
              Press Enter to send, Shift+Enter for new line
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

// =============================================================================
// x402 Payment Helpers
// =============================================================================

const NETWORK_NAMES: Record<string, string> = {
  'eip155:1': 'Ethereum',
  'eip155:8453': 'Base',
  'eip155:84532': 'Base Sepolia',
  'eip155:11155111': 'Sepolia',
  'eip155:137': 'Polygon',
}

const KNOWN_ASSETS: Record<string, { name: string; decimals: number }> = {
  '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913': { name: 'USDC', decimals: 6 },
  '0x036cbd53842c5426634e7929541ec2318f3dcf7e': { name: 'USDC', decimals: 6 },
  '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48': { name: 'USDC', decimals: 6 },
  '0x3c499c542cef5e3811e1192ce70d8cc03d5c3359': { name: 'USDC', decimals: 6 },
}

function formatPaymentSummary(info: Record<string, unknown> | undefined): string | null {
  if (!info) return null
  try {
    const accepts = info['accepts'] as Array<Record<string, unknown>> | undefined
    if (accepts && Array.isArray(accepts) && accepts.length > 0) {
      const options = accepts.map(offer => {
        const amount = offer['amount'] as string | undefined
        const network = offer['network'] as string | undefined
        const asset = (offer['asset'] as string || '').toLowerCase()
        const extra = offer['extra'] as Record<string, unknown> | undefined
        const tokenName = extra?.['name'] as string || KNOWN_ASSETS[asset]?.name || 'tokens'
        const decimals = KNOWN_ASSETS[asset]?.decimals ?? 18
        const networkName = network ? (NETWORK_NAMES[network] || network) : ''

        if (amount) {
          const formatted = (Number(amount) / Math.pow(10, decimals)).toString()
          return `${formatted} ${tokenName}${networkName ? ` on ${networkName}` : ''}`
        }
        return networkName ? `${tokenName} on ${networkName}` : tokenName
      })
      return options.length === 1 ? options[0] : options.join(' or ')
    }

    const x402 = info['x402'] as Record<string, unknown> | undefined
    if (x402) {
      const price = x402['price'] as string | undefined
      const currency = x402['currency'] as string | undefined
      if (price && currency) return `${price} ${currency}`
    }

    return null
  } catch {
    return null
  }
}

function extractPayTo(info: Record<string, unknown> | undefined): string | null {
  if (!info) return null
  try {
    const accepts = info['accepts'] as Array<Record<string, unknown>> | undefined
    if (accepts && Array.isArray(accepts) && accepts.length > 0) {
      const payTo = accepts[0]['payTo'] as string | undefined
      return payTo || null
    }
    return null
  } catch {
    return null
  }
}

function extractNetworks(info: Record<string, unknown> | undefined): string | null {
  if (!info) return null
  try {
    const accepts = info['accepts'] as Array<Record<string, unknown>> | undefined
    if (accepts && Array.isArray(accepts) && accepts.length > 0) {
      const networks = [...new Set(accepts.map(a => {
        const network = a['network'] as string | undefined
        return network ? (NETWORK_NAMES[network] || network) : null
      }).filter(Boolean))]
      return networks.length > 0 ? networks.join(', ') : null
    }
    return null
  } catch {
    return null
  }
}

// Icons
function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  )
}

function LoadingDots() {
  return (
    <span className="inline-flex gap-0.5">
      <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </span>
  )
}
